import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Palette,
  Code,
  Shield,
  FileCheck,
  HeadphonesIcon,
  Check,
  Loader2,
  AlertCircle,
  Crown,
  ArrowRight,
  Sparkles,
  Award,
  Clock,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIsMobile } from "@/hooks/useMobileUtils";
import { billingApi, getErrorMessage } from "@/services/api";
import { generateUUID } from "@/utils/transform";

const ADDON_ICONS = {
  white_label: Palette,
  branding_essentials: Palette,
  branding_pro: Palette,
  api_access: Code,
  api_standard: Code,
  api_pro: Code,
  security: Shield,
  security_standard: Shield,
  compliance: FileCheck,
  compliance_hipaa: FileCheck,
  priority_support: HeadphonesIcon,
  support_priority: HeadphonesIcon,
  certification_timestamp: Clock,
  certification_qualified: Award,
};

const ADDON_COLORS = {
  white_label: "text-purple-500",
  branding_essentials: "text-purple-500",
  branding_pro: "text-purple-500",
  api_access: "text-blue-500",
  api_standard: "text-blue-500",
  api_pro: "text-blue-500",
  security: "text-green-500",
  security_standard: "text-green-500",
  compliance: "text-orange-500",
  compliance_hipaa: "text-orange-500",
  priority_support: "text-pink-500",
  support_priority: "text-pink-500",
  certification_timestamp: "text-amber-500",
  certification_qualified: "text-primary",
};

export const Addons = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [cancelDialog, setCancelDialog] = useState({
    open: false,
    addon: null,
  });
  const [activeTab, setActiveTab] = useState(
    searchParams.get("addon") || "all",
  );

  // ============================================
  // TanStack Query - Fetch Addons
  // ============================================
  const { data: addonsData, isLoading: loading } = useQuery({
    queryKey: ["billing-addons"],
    queryFn: async () => {
      const [availableRes, activeRes] = await Promise.all([
        billingApi.getAddons(),
        billingApi.getActiveAddons(),
      ]);
      return {
        availableAddons: availableRes.data.data || [],
        activeAddons: activeRes.data.data || [],
      };
    },
    meta: {
      onError: () => {
        toast.error(t("toast.failedLoadAddons"));
      },
    },
  });

  const availableAddons = addonsData?.availableAddons || [];
  const activeAddons = addonsData?.activeAddons || [];

  // ============================================
  // TanStack Query - Mutations
  // ============================================
  const upgradeMutation = useMutation({
    mutationFn: ({ addonId, tierId }) =>
      billingApi.upgradeAddonTier(addonId, tierId),
    onSuccess: () => {
      toast.success(t("toast.addonUpgraded"));
      queryClient.invalidateQueries({ queryKey: ["billing-addons"] });
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, t("errors.failedUpgradeAddon")));
    },
    onSettled: () => {
      setCheckoutLoading(null);
    },
  });

  const cancelMutation = useMutation({
    mutationFn: ({ addonType, immediately }) =>
      billingApi.cancelAddon(addonType, immediately),
    onSuccess: (_, { immediately }) => {
      toast.success(
        immediately
          ? t("billing.addonCanceled")
          : t("billing.addonCanceledEndOfPeriod"),
      );
      setCancelDialog({ open: false });
      queryClient.invalidateQueries({ queryKey: ["billing-addons"] });
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, t("errors.failedCancelAddon")));
    },
    onSettled: () => {
      setCheckoutLoading(null);
    },
  });

  useEffect(() => {
    if (searchParams.get("success") === "true") {
      toast.success(t("toast.addonActivated"));
    } else if (searchParams.get("canceled") === "true") {
      toast.error(t("toast.addonPurchaseCanceled"));
    }
  }, [searchParams, t]);

  const getActiveAddon = (addonId) =>
    activeAddons.find((a) => a.addonType === addonId);

  const handleSubscribe = async (addonId, tierId) => {
    try {
      setCheckoutLoading(`${addonId}-${tierId}`);
      const response = await billingApi.createAddonCheckout(addonId, tierId);
      window.location.href = response.data.data.url;
    } catch (err) {
      toast.error(getErrorMessage(err, t("errors.failedStartCheckout")));
      setCheckoutLoading(null);
    }
  };

  const handleUpgrade = (addonId, tierId) => {
    setCheckoutLoading(`${addonId}-${tierId}`);
    upgradeMutation.mutate({ addonId, tierId });
  };

  const handleCancel = (addonType, immediately = false) => {
    setCheckoutLoading(addonType);
    cancelMutation.mutate({ addonType, immediately });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-100">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">
            {t("billing.addons.title")}
          </h2>
          <p className="text-muted-foreground">
            Enhance your eScribe experience with powerful add-ons
          </p>
        </div>
      </div>

      {/* Active Add-ons Summary */}
      {activeAddons.length > 0 && (
        <Alert>
          <Crown className="h-4 w-4" />
          <AlertDescription>
            You have {activeAddons.length} active add-on
            {activeAddons.length > 1 ? "s" : ""}:{" "}
            {activeAddons.map((a) => a.addonDetails?.tierName).join(", ")}
          </AlertDescription>
        </Alert>
      )}

      {/* Add-on Tabs */}
      {availableAddons.length === 0 ? (
        <Card className="p-8">
          <div className="text-center space-y-4">
            <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center">
              <Sparkles className="h-6 w-6 text-muted-foreground" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">No add-ons available</h3>
              <p className="text-muted-foreground text-sm mt-1">
                Add-ons will appear here once they are configured. Check back
                later or contact support.
              </p>
            </div>
          </div>
        </Card>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="all">
              {t("billing.addons.allAddons")}
            </TabsTrigger>
            {availableAddons.map((addon) => (
              <TabsTrigger key={addon.id} value={addon.id}>
                {addon.name}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="all" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {availableAddons.map((addon) => (
                <AddonCard
                  key={addon.id}
                  addon={addon}
                  activeAddon={getActiveAddon(addon.id)}
                  onSubscribe={handleSubscribe}
                  onUpgrade={handleUpgrade}
                  onCancel={(addonType) =>
                    setCancelDialog({ open: true, addon: addonType })
                  }
                  checkoutLoading={checkoutLoading}
                />
              ))}
            </div>
          </TabsContent>

          {availableAddons.map((addon) => (
            <TabsContent key={addon.id} value={addon.id} className="mt-6">
              <AddonDetailView
                addon={addon}
                activeAddon={getActiveAddon(addon.id)}
                onSubscribe={handleSubscribe}
                onUpgrade={handleUpgrade}
                onCancel={(addonType) =>
                  setCancelDialog({ open: true, addon: addonType })
                }
                checkoutLoading={checkoutLoading}
              />
            </TabsContent>
          ))}
        </Tabs>
      )}

      {/* Cancel Dialog */}
      <Dialog
        open={cancelDialog.open}
        onOpenChange={(open) => setCancelDialog({ ...cancelDialog, open })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("billing.addons.cancelAddon")}</DialogTitle>
            <DialogDescription>
              {t("billing.addons.cancelAddonDesc")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setCancelDialog({ open: false })}
              size={isMobile ? "mobile" : "default"}
            >
              Keep Add-on
            </Button>
            <Button
              variant="secondary"
              onClick={() => handleCancel(cancelDialog.addon, false)}
              disabled={checkoutLoading}
              size={isMobile ? "mobile" : "default"}
            >
              Cancel at Period End
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleCancel(cancelDialog.addon, true)}
              disabled={checkoutLoading}
              size={isMobile ? "mobile" : "default"}
            >
              Cancel Immediately
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const AddonCard = ({
  addon,
  activeAddon,
  onSubscribe,
  onUpgrade,
  onCancel,
  checkoutLoading,
}) => {
  const { t } = useTranslation();
  const Icon = ADDON_ICONS[addon.id] || Sparkles;
  const colorClass = ADDON_COLORS[addon.id] || "text-primary";
  const tiers = addon.tiers || [];
  const activeTier = activeAddon?.addonTier;

  const renderAddonButtons = (tier, isActive, isUpgrade) => (
    <>
      <Button
        size="sm"
        className="w-full"
        onClick={() => onSubscribe(addon.id, tier.id)}
        disabled={checkoutLoading === `${addon.id}-${tier.id}`}
      >
        {checkoutLoading === `${addon.id}-${tier.id}` && (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        )}
        Subscribe
      </Button>
      {isActive ? (
        <Button
          size="sm"
          variant="outline"
          className="w-full"
          onClick={() => onCancel(addon.id)}
        >
          Cancel
        </Button>
      ) : null}
      {isUpgrade ? (
        <Button
          size="sm"
          className="w-full"
          onClick={() => onUpgrade(addon.id, tier.id)}
          disabled={checkoutLoading === `${addon.id}-${tier.id}`}
        >
          {checkoutLoading === `${addon.id}-${tier.id}` && (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          )}
          Upgrade
        </Button>
      ) : null}
    </>
  );

  return (
    <Card className={activeAddon ? "border-primary" : ""}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg bg-muted ${colorClass}`}>
              <Icon className="h-6 w-6" />
            </div>
            <div>
              <CardTitle className="flex items-center gap-2">
                {addon.name}
                {activeAddon && (
                  <Badge variant="success">{t("common.active")}</Badge>
                )}
              </CardTitle>
              <CardDescription>{addon.description}</CardDescription>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {tiers.map((tier) => {
            const isActive = activeTier === tier.id;
            const isUpgrade =
              activeTier &&
              !isActive &&
              tiers.findIndex((t) => t.id === tier.id) >
                tiers.findIndex((t) => t.id === activeTier);

            return (
              <div
                key={tier.id}
                className={`p-4 rounded-lg border ${
                  isActive ? "border-primary bg-primary/5" : "border-border"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{tier.name}</span>
                    {tier.popular && (
                      <Badge variant="secondary">{t("common.popular")}</Badge>
                    )}
                    {isActive && (
                      <Badge variant="success">{t("common.current")}</Badge>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="font-bold">
                      ${tier.priceMonthlyDisplay}/mo
                    </div>
                    {tier.perEnvelopeCost && (
                      <div className="text-xs text-muted-foreground">
                        +${(tier.perEnvelopeCost / 100).toFixed(2)}/envelope
                      </div>
                    )}
                    {tier.perSignatureCost && (
                      <div className="text-xs text-muted-foreground">
                        +${(tier.perSignatureCost / 100).toFixed(2)}/signature
                      </div>
                    )}
                  </div>
                </div>
                <ul className="text-sm text-muted-foreground space-y-1 mb-3">
                  {tier.featureList?.slice(0, 3).map((feature) => (
                    <li
                      key={generateUUID()}
                      className="flex items-center gap-2"
                    >
                      <Check className="h-3 w-3 text-green-500" />
                      {feature}
                    </li>
                  ))}
                </ul>
                {!activeAddon
                  ? renderAddonButtons(tier, isActive, isUpgrade)
                  : null}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const AddonDetailView = ({
  addon,
  activeAddon,
  onSubscribe,
  onUpgrade,
  onCancel,
  checkoutLoading,
}) => {
  const { t } = useTranslation();
  const Icon = ADDON_ICONS[addon.id] || Sparkles;
  const colorClass = ADDON_COLORS[addon.id] || "text-primary";
  const tiers = addon.tiers || [];
  const activeTier = activeAddon?.addonTier;

  const renderAddonDetailViewButtons = (tier, isActive, canUpgrade) => {
    if (!activeAddon) {
      return (
        <Button
          className="w-full"
          variant={tier.popular ? "default" : "secondary"}
          onClick={() => onSubscribe(addon.id, tier.id)}
          disabled={checkoutLoading === `${addon.id}-${tier.id}`}
        >
          {checkoutLoading === `${addon.id}-${tier.id}` && (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          )}
          Get Started
        </Button>
      );
    }
    if (isActive) {
      return (
        <Button
          className="w-full"
          variant="outline"
          onClick={() => onCancel(addon.id)}
        >
          Cancel Subscription
        </Button>
      );
    }
    return canUpgrade ? (
      <Button
        className="w-full"
        onClick={() => onUpgrade(addon.id, tier.id)}
        disabled={checkoutLoading === `${addon.id}-${tier.id}`}
      >
        {checkoutLoading === `${addon.id}-${tier.id}` && (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        )}
        Upgrade <ArrowRight className="h-4 w-4 ml-2" />
      </Button>
    ) : (
      <Button className="w-full" variant="ghost" disabled>
        Included in Current Tier
      </Button>
    );
  };

  return (
    <div className="space-y-6">
      {/* Addon Header */}
      <div className="flex items-start gap-4">
        <div className={`p-3 rounded-xl bg-muted ${colorClass}`}>
          <Icon className="h-8 w-8" />
        </div>
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            {addon.name}
            {activeAddon && (
              <Badge variant="success">{t("common.active")}</Badge>
            )}
          </h2>
          <p className="text-muted-foreground">{addon.description}</p>
        </div>
      </div>

      {/* Tiers Comparison */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {tiers.map((tier, idx) => {
          const isActive = activeTier === tier.id;
          const canUpgrade =
            activeTier &&
            !isActive &&
            idx > tiers.findIndex((t) => t.id === activeTier);

          return (
            <Card
              key={tier.id}
              className={`relative ${
                tier.popular ? "border-primary shadow-lg" : ""
              } ${isActive ? "ring-2 ring-primary" : ""}`}
            >
              {tier.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-primary">
                    {t("common.mostPopular")}
                  </Badge>
                </div>
              )}
              {isActive && (
                <div className="absolute -top-3 right-4">
                  <Badge variant="success">{t("common.current")}</Badge>
                </div>
              )}

              <CardHeader className="text-center">
                <CardTitle>{tier.name}</CardTitle>
                <CardDescription>{tier.description}</CardDescription>
                <div className="mt-4">
                  <div className="text-4xl font-bold">
                    ${tier.priceMonthlyDisplay}
                  </div>
                  <div className="text-sm text-muted-foreground">/month</div>
                  {tier.perEnvelopeCost && (
                    <div className="text-xs text-muted-foreground mt-1">
                      +${(tier.perEnvelopeCost / 100).toFixed(2)} per envelope
                    </div>
                  )}
                  {tier.perSignatureCost && (
                    <div className="text-xs text-muted-foreground mt-1">
                      +${(tier.perSignatureCost / 100).toFixed(2)} per signature
                    </div>
                  )}
                </div>
              </CardHeader>

              <CardContent>
                <ul className="space-y-3">
                  {tier.featureList?.map((feature) => (
                    <li key={generateUUID()} className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>

              <CardFooter>
                {renderAddonDetailViewButtons(tier, isActive, canUpgrade)}
              </CardFooter>
            </Card>
          );
        })}
      </div>

      {/* Active Subscription Info */}
      {activeAddon && (
        <Card>
          <CardHeader>
            <CardTitle>{t("billing.yourSubscription")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-muted-foreground">
                  {t("common.status")}
                </div>
                <div className="font-medium">{activeAddon.status}</div>
              </div>
              <div>
                <div className="text-muted-foreground">
                  {t("billing.currentTier")}
                </div>
                <div className="font-medium">
                  {activeAddon.addonDetails?.tierName}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">{t("common.price")}</div>
                <div className="font-medium">
                  ${activeAddon.addonDetails?.priceMonthly}/month
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">
                  {t("billing.nextBilling")}
                </div>
                <div className="font-medium">
                  {activeAddon.currentPeriodEnd
                    ? new Date(
                        activeAddon.currentPeriodEnd,
                      ).toLocaleDateString()
                    : "N/A"}
                </div>
              </div>
            </div>
            {activeAddon.cancelAtPeriodEnd && (
              <Alert className="mt-4" variant="warning">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  This add-on is set to cancel at the end of your billing
                  period.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Addons;
